/**
 * Pure calculation functions for rim-to-invert measurements
 *
 * Based on Clay Prewitt Titan demo (January 22, 2026):
 * - Rim-to-invert = rim_elevation - min(all_pipe_inverts)
 * - Base contributes: invertToFirstJoint - (annularSpace / 12)
 * - All other components contribute their FULL nominal height (NO joint overlap deduction)
 * - JOINT_OVERLAP_INCHES is only used for core drill position calculations (manufacturing)
 *
 * Validated against Clay's exact Titan inputs:
 *   Rim 675.30, Invert 665.67 (R-I = 9.63 ft)
 *   Base(2.583) + Riser(3.0) + AdjRing(0.5) + Cone(3.0) + Casting(0.5) = 9.583
 *   Remaining = 0.047 ≈ 0.05 ft — matches Titan
 */

import { JOINT_OVERLAP_INCHES } from "../../../../types/manhole.types";

/**
 * @deprecated This function conflates manufacturing (core drill) with surveying (invert).
 * Use invertPositionInches directly instead of calculating it from core drill + annular space.
 *
 * For manufacturing core drill calculations, use the functions in manhole.types.ts instead.
 */
export function calculateInvertHeight(
  coreDrillHeightInches: number,
  annularSpaceInches: number
): number {
  return coreDrillHeightInches + annularSpaceInches;
}

/**
 * Calculate base component contribution to rim-to-invert height
 *
 * SIMPLIFIED per Scott Stavig (January 13, 2026):
 * - Use invert position directly (where the pipe flow line is)
 * - No joint height - joints overlap when stacked, they don't add to height
 * - No annular space - that's a manufacturing concern
 * - Floor thickness doesn't matter - invert is always above it
 *
 * Formula:
 *   baseContribution = (baseNominalHeight - invertPositionInches) / 12
 *
 * Example for 48" x 3' base with invert at 8" up:
 *   - Base nominal: 36"
 *   - Invert position: 8" (where inside bottom of pipe sits)
 *   - Contribution: (36 - 8) / 12 = 28/12 = 2.33 feet
 *
 * @param baseNominalHeightInches - Nominal base height
 * @param invertPositionInches - Where the pipe invert (flow line) is from bottom of base
 * @returns Base contribution to rim-to-invert in feet
 */
export function calculateBaseContribution(
  baseNominalHeightInches: number,
  invertPositionInches: number
): number {
  // Contribution is height above invert (no joint added - joints overlap when stacked)
  const contributionInches = baseNominalHeightInches - invertPositionInches;
  return contributionInches / 12;
}

/**
 * Calculate component contribution for non-base components
 *
 * Per Clay Prewitt (2026-01-22 Titan demo):
 * Components contribute their FULL NOMINAL HEIGHT to the stack.
 * Titan does NOT deduct joint overlap between stacked components.
 *
 * Clay's validated example (9.63 ft rim-to-invert):
 *   Base (2.583 effective) + 3' riser (3.0) + 6" adj (0.5) + 3' cone (3.0) + casting (0.5)
 *   = 9.583 ft filled, remaining 0.05 ft — matches Titan exactly.
 *
 * @param heightInches - Component nominal height
 * @param _isFirstComponent - Unused (kept for API compatibility)
 * @returns Component contribution in feet (full nominal height)
 */
export function calculateComponentContribution(
  heightInches: number,
  _isFirstComponent: boolean = false
): number {
  return heightInches / 12;
}

/**
 * Calculate riser core drill position adjustment
 *
 * NOTE: This is a MANUFACTURING function, not a rim-to-invert function.
 * It tells production where to drill the hole in a riser section.
 *
 * Per Clay (December 17, 2025):
 * "When it's a riser section... If it says 8 inches up, you have to add
 * the three inches to it because it's not part of the stack."
 *
 * For risers, the drawing specification is from inside top of bell,
 * so workers must add the joint height.
 *
 * @param drawingSpecInches - Hole position as shown on drawing
 * @param jointHeightInches - Joint height to add (typically 3")
 * @returns Actual drill position from bottom of riser section
 */
export function calculateRiserCoreDrillPosition(
  drawingSpecInches: number,
  jointHeightInches: number = JOINT_OVERLAP_INCHES
): number {
  return drawingSpecInches + jointHeightInches;
}

/**
 * Calculate remaining rim-to-invert height
 *
 * @param totalRimToInvertFeet - Total rim-to-invert requirement
 * @param filledHeightFeet - Height already filled by components
 * @returns Remaining height to fill in feet
 */
export function calculateRemainingRimToInvert(
  totalRimToInvertFeet: number,
  filledHeightFeet: number
): number {
  return Math.max(0, totalRimToInvertFeet - filledHeightFeet);
}

/**
 * Calculate total structure height (rim-to-invert + height below invert)
 *
 * @param rimToInvertFeet - Rim-to-invert distance
 * @param invertPositionInches - Invert position from outside bottom of base
 * @returns Total structure height in feet
 */
export function calculateTotalStructureHeight(
  rimToInvertFeet: number,
  invertPositionInches: number
): number {
  return rimToInvertFeet + invertPositionInches / 12;
}

/**
 * Calculate filled height from a stack of components
 *
 * @param components - Array of components with type and height
 * @param invertPositionInches - Where the pipe invert is in the base
 * @returns Total filled height in feet
 */
export function calculateFilledHeightFromComponents(
  components: Array<{
    type: "BASE" | "RISER" | "CONE" | "ADJUSTMENT" | string;
    nominalHeightInches: number;
  }>,
  invertPositionInches: number
): number {
  let filledHeightFeet = 0;

  components.forEach((comp, index) => {
    const compType = comp.type.toUpperCase();

    if (index === 0 && (compType === "BASE" || compType.includes("BASE"))) {
      // Base: simplified formula (no joint, no annular space)
      filledHeightFeet += calculateBaseContribution(
        comp.nominalHeightInches,
        invertPositionInches
      );
    } else {
      // Per Clay Prewitt (2026-01-22): full nominal height, no joint overlap deduction
      filledHeightFeet += calculateComponentContribution(
        comp.nominalHeightInches,
        index === 0
      );
    }
  });

  return filledHeightFeet;
}

/**
 * Calculate rim-to-invert from survey elevations
 *
 * Per Scott Stavig (January 13, 2026):
 * "rim_to_invert = rim_elevation - min(all_pipe_inverts)"
 *
 * This is a PURE ELEVATION DIFFERENCE. No annular space, hole size,
 * boot size, or joint height factors into this calculation.
 *
 * @param rimElevationFeet - Rim elevation (finished grade, top of casting)
 * @param pipeInvertElevations - Array of pipe invert elevations (flow lines)
 * @returns Rim-to-invert distance in feet
 */
export function calculateRimToInvertFromElevations(
  rimElevationFeet: number,
  pipeInvertElevations: number[]
): number {
  if (pipeInvertElevations.length === 0) return 0;

  const lowestInvert = Math.min(...pipeInvertElevations);
  return Math.max(0, rimElevationFeet - lowestInvert);
}

// ============================================
// CANVAS POSITIONING FUNCTIONS
// ============================================

/**
 * Calculate pipe offset below rim from survey elevations
 *
 * Per Scott Stavig (January 13, 2026):
 * Rim elevation is the DATUM. All pipe positions are calculated as
 * differences below the rim.
 *
 * @param rimElevationFeet - Rim elevation (survey value, e.g., 14.81)
 * @param pipeInvertElevationFeet - Pipe invert elevation (survey value, e.g., 10.15)
 * @returns Offset below rim in feet (e.g., 4.66)
 *
 * @example
 * // Rim at 14.81 ft, pipe invert at 10.15 ft
 * calculatePipeOffsetBelowRim(14.81, 10.15) // Returns 4.66
 */
export function calculatePipeOffsetBelowRim(
  rimElevationFeet: number,
  pipeInvertElevationFeet: number
): number {
  return Math.max(0, rimElevationFeet - pipeInvertElevationFeet);
}

/**
 * Calculate pipe position from bottom of structure for canvas rendering
 *
 * Given:
 * - Total stack height (how tall the structure is)
 * - Offset below rim (how far the pipe is from the top)
 *
 * Returns the pipe's position measured from the BOTTOM of the structure.
 *
 * @param totalStackHeightFeet - Total height of the component stack
 * @param offsetBelowRimFeet - How far the pipe is below the rim
 * @returns Position from bottom of structure in feet
 *
 * @example
 * // 6ft structure, pipe is 4.66ft below rim
 * calculatePipePositionFromBottom(6.0, 4.66) // Returns 1.34
 * // Pipe is 1.34 ft up from the bottom of the structure
 */
export function calculatePipePositionFromBottom(
  totalStackHeightFeet: number,
  offsetBelowRimFeet: number
): number {
  return Math.max(0, totalStackHeightFeet - offsetBelowRimFeet);
}

/**
 * Calculate pipe canvas position from survey elevations
 *
 * Convenience function that combines offset calculation and position from bottom.
 * Used for canvas rendering to convert survey elevations to structure-relative positions.
 *
 * @param rimElevationFeet - Rim elevation (survey value)
 * @param pipeInvertElevationFeet - Pipe invert elevation (survey value)
 * @param totalStackHeightFeet - Total height of component stack
 * @returns Object with offset below rim and position from bottom
 *
 * @example
 * // Rim=14.81, pipe invert=10.15, stack height=6ft
 * calculatePipeCanvasPosition(14.81, 10.15, 6.0)
 * // Returns { offsetBelowRim: 4.66, positionFromBottom: 1.34 }
 */
export function calculatePipeCanvasPosition(
  rimElevationFeet: number,
  pipeInvertElevationFeet: number,
  totalStackHeightFeet: number
): { offsetBelowRim: number; positionFromBottom: number } {
  const offsetBelowRim = calculatePipeOffsetBelowRim(
    rimElevationFeet,
    pipeInvertElevationFeet
  );
  const positionFromBottom = calculatePipePositionFromBottom(
    totalStackHeightFeet,
    offsetBelowRim
  );
  return { offsetBelowRim, positionFromBottom };
}
